/**
 * Mobile grooming through the CMS (CMS G26; mpgadmin D113–D119). While the
 * API's `Mobile__Enabled` is off every route answers 404, and the site books
 * at the salon exactly as before: `visitTypes` reads that as "nothing to ask".
 * Every amount and sentence is the server's (G42).
 */
import { CMS_BASE_URL, CMS_ENVIRONMENT_QUERY } from '../config/api.config';
import { getCMSJWT } from './auth.service';
import type {
  AddressSuggestion,
  BackAboutSlot,
  ClientAddress,
  SaveAddress,
  TravelBands,
  TravelQuote,
  VisitType,
  VisitTypeChoice,
  ZoneDays,
} from '../types/mobile.types';

/** The server's sentence for a refusal ("Pick the address from the suggestions."), with its code. */
export class MobileRefusal extends Error {
  constructor(message: string, readonly code: string | null, readonly status: number) {
    super(message);
    this.name = 'MobileRefusal';
  }
}

const refusal = async (res: Response): Promise<MobileRefusal> => {
  try {
    const body = await res.json();
    return new MobileRefusal(
      typeof body?.error === 'string' && body.error ? body.error : 'That didn’t work. Please try again.',
      typeof body?.code === 'string' ? body.code : null,
      res.status
    );
  } catch {
    return new MobileRefusal('That didn’t work. Please try again.', null, res.status);
  }
};

const read = async <T,>(res: Response): Promise<T> => {
  if (!res.ok) throw await refusal(res);
  return (await res.json()) as T;
};

const authed = (path: string, init: RequestInit = {}): Promise<Response> => {
  const jwt = getCMSJWT();
  if (!jwt) return Promise.reject(new MobileRefusal('Sign in to continue.', 'not_signed_in', 401));
  return fetch(`${CMS_BASE_URL}${path}`, {
    cache: 'no-store',
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}`, ...(init.headers ?? {}) },
  });
};

/** A public route; every path names its business, so the environment joins its query. */
const pub = (path: string, init: RequestInit = {}): Promise<Response> =>
  fetch(`${CMS_BASE_URL}${path}${CMS_ENVIRONMENT_QUERY}`, {
    cache: 'no-store',
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
  });

/** The visit types by location, read once while the page is open. */
const typesCache = new Map<string, Promise<VisitTypeChoice[]>>();

export const mobileService = {
  /**
   * The location's visit types; [] when mobile is off (404), the location
   * offers none, or the read failed. A failure books at the salon, as today.
   */
  visitTypes(companyId: number, storeId: number): Promise<VisitTypeChoice[]> {
    const key = `${companyId}:${storeId}`;
    let hit = typesCache.get(key);
    if (!hit) {
      hit = pub(`/api/mobile/visit-types?companyId=${companyId}&storeId=${storeId}`)
        .then(res => (res.ok ? res.json() : []))
        .then(list => (Array.isArray(list) ? (list as VisitTypeChoice[]) : []))
        .catch(() => []);
      typesCache.set(key, hit);
      // A failed read is asked again next time.
      hit.then(list => list.length === 0 && typesCache.delete(key));
    }
    return hit;
  },

  /** The location's travel fees (SF 101–103, 110); null when unknown. */
  async bands(companyId: number, storeId: number): Promise<TravelBands | null> {
    try {
      const res = await pub(`/api/mobile/bands?companyId=${companyId}&storeId=${storeId}`);
      return res.ok ? ((await res.json()) as TravelBands) : null;
    } catch {
      return null;
    }
  },

  /** Google's suggestions as the visitor types (Australia only). */
  async suggest(companyId: number, input: string, sessionToken: string): Promise<AddressSuggestion[]> {
    const q = new URLSearchParams({ companyId: String(companyId), input, sessionToken });
    const res = await pub(`/api/mobile/address/suggest?${q}`);
    return res.ok ? ((await res.json()) as AddressSuggestion[]) : [];
  },

  /**
   * The travel line for a Google place: signed out (by place only), or signed
   * in (`my-quote`, which may name a saved address, the drop-off and the pet).
   */
  quote(
    companyId: number,
    body: {
      storeId: number;
      visitType: VisitType;
      placeId?: string;
      sessionToken?: string;
      addressId?: number;
      dropOffAddressId?: number;
      dropOffPlaceId?: string;
      petId?: number;
      /** "2026-11-03", the day picked: a zone's day price applies (mpgadmin D148). */
      date?: string;
    },
    signedIn: boolean
  ): Promise<TravelQuote> {
    const init = { method: 'POST', body: JSON.stringify(body) };
    return (signedIn ? authed('/api/mobile/my-quote', init) : pub(`/api/mobile/quote?companyId=${companyId}`, init)).then(
      res => read<TravelQuote>(res)
    );
  },

  /**
   * The van's days for the visit's address (mpgadmin G226, CMS G32): a saved
   * address when signed in, else the picked place's text. Null when there is no
   * zone, the routes are off (404) or the read failed: then nothing is marked.
   */
  async zoneDays(companyId: number, storeId: number, at: { addressId: number } | { address: string }): Promise<ZoneDays | null> {
    try {
      const res =
        'addressId' in at
          ? await authed(`/api/mobile/my-zone-days?storeId=${storeId}&addressId=${at.addressId}`)
          : await pub(`/api/mobile/zone-days?${new URLSearchParams({ companyId: String(companyId), storeId: String(storeId), address: at.address })}`);
      if (!res.ok) return null;
      const z = (await res.json()) as ZoneDays;
      const weekdays = Array.isArray(z?.weekdays) ? z.weekdays.filter(d => Number.isInteger(d) && d >= 1 && d <= 7) : [];
      return weekdays.length ? { weekdays, words: typeof z.words === 'string' && z.words.trim() ? z.words.trim() : null } : null;
    } catch {
      return null;
    }
  },

  /** A pick-up's start times with when the pet is back home (SF 112). */
  backAbout(
    companyId: number,
    body: { storeId: number; groomMinutes: number; startsUtc: string[]; visit: Record<string, unknown> },
    signedIn: boolean
  ): Promise<BackAboutSlot[]> {
    const init = { method: 'POST', body: JSON.stringify(body) };
    return (signedIn
      ? authed('/api/mobile/my-back-about', init)
      : pub(`/api/mobile/back-about?companyId=${companyId}`, init)
    ).then(res => read<BackAboutSlot[]>(res));
  },

  /**
   * Who'll be home, or who hands over, on the client's booking (booked page
   * "Change", SF 108, 113; mpgadmin G173). Neither name nor mobile is the
   * client themselves. A refusal comes back in the server's words.
   */
  changeHandover: (
    appointmentId: number,
    body: { handoverName?: string; handoverPhone?: string; saveHandoverAsUsual?: boolean }
  ): Promise<{ handoverName?: string | null; handoverPhone?: string | null }> =>
    authed(`/api/mobile/bookings/${appointmentId}/handover`, { method: 'PUT', body: JSON.stringify(body) }).then(res =>
      read<{ handoverName?: string | null; handoverPhone?: string | null }>(res)
    ),

  /** The signed-in client's addresses at this business, kept in memory as last read (G47). */
  addresses: (): Promise<ClientAddress[]> =>
    authed('/api/mobile/addresses')
      .then(res => read<ClientAddress[]>(res))
      .then(list => keepAddresses(list)),

  /** Their addresses as last read this visit, for drawing at once; null before the first read or after sign-out. */
  addressesAsRead: (): ClientAddress[] | null => (kept && kept.who === getCMSJWT() ? kept.list : null),

  addAddress: (body: SaveAddress): Promise<ClientAddress> =>
    authed('/api/mobile/addresses', { method: 'POST', body: JSON.stringify(body) })
      .then(res => read<ClientAddress>(res))
      .then(keepAddress),

  updateAddress: (id: number, body: SaveAddress): Promise<ClientAddress> =>
    authed(`/api/mobile/addresses/${id}`, { method: 'PUT', body: JSON.stringify(body) })
      .then(res => read<ClientAddress>(res))
      .then(keepAddress),

  async removeAddress(id: number): Promise<void> {
    const res = await authed(`/api/mobile/addresses/${id}`, { method: 'DELETE' });
    if (!res.ok && res.status !== 204) throw await refusal(res);
    if (kept) kept = { ...kept, list: kept.list.filter(a => a.id !== id) };
  },
};

/**
 * The client's addresses as last read, in memory only and for the signed-in
 * person who read them (G47): the Your address sheet draws them at once and
 * reads them again behind. Never a drive time (mpgadmin G172).
 */
let kept: { who: string | null; list: ClientAddress[] } | null = null;

const keepAddresses = (list: ClientAddress[]): ClientAddress[] => {
  kept = { who: getCMSJWT(), list };
  return list;
};

/** A save: the server may hand back one they already had (mpgadmin G178), so it replaces, never repeats. */
const keepAddress = (a: ClientAddress): ClientAddress => {
  if (kept && kept.who === getCMSJWT()) {
    const others = kept.list.filter(x => x.id !== a.id).map(x => (a.isDefault ? { ...x, isDefault: false } : x));
    kept = { ...kept, list: kept.list.some(x => x.id === a.id) ? kept.list.map(x => (x.id === a.id ? a : others.find(o => o.id === x.id)!)) : [...others, a] };
  }
  return a;
};

/** Sign-out (G1): their addresses go with them. */
export const forgetAddresses = (): void => {
  kept = null;
};

/** A Places session token: one per typing, ended by the save or quote that uses it. */
export const newPlacesToken = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().replace(/-/g, '')
    : `t${Date.now()}${Math.random().toString(36).slice(2)}`;
